/**
 * Centralized React Flow Canvas Types
 *
 * Domain-agnostic, data-driven types that support all 25 primitive workflow types.
 * All data comes from database/parameters - no hardcoded domain logic.
 * Life-critical system: maintains all metadata fields for auditing and traceability.
 */

import { Edge as ReactFlowEdge, Node as ReactFlowNode } from 'reactflow';

/**
 * 25 Primitive Workflow Types — FERP's actual primitive system.
 *
 * Organized by 5 lifecycle phases: DESIGN, CONFIGURE, EXECUTE, MONITOR, OPTIMIZE.
 * Legacy names (WorkflowDefinition, ActorDescriptor, etc.) retained for backward
 * compatibility with existing canvas data. New code should use lowercase names
 * matching primitive-metadata.ts.
 */
export type PrimitiveWorkflowType =
  // ── DESIGN phase (5) ───────────────────────────────────────────────
  | 'workflow' | 'WorkflowDefinition'       // ← legacy alias
  | 'template'
  | 'dependency' | 'TransitionCatalog'      // ← legacy alias
  | 'policy' | 'Policy'                     // ← legacy alias
  | 'role'
  // ── CONFIGURE phase (5) ────────────────────────────────────────────
  | 'team'
  | 'actor' | 'ActorDescriptor'             // ← legacy alias
  | 'validation'
  | 'contract' | 'Context'                  // ← legacy alias
  | 'constraint'
  // ── EXECUTE phase (5) ──────────────────────────────────────────────
  | 'instance' | 'Instance'                 // ← legacy alias
  | 'task' | 'Task'                         // ← legacy alias
  | 'approval' | 'Escalation'              // ← legacy alias
  | 'document'
  | 'queue'
  // ── MONITOR phase (5) ──────────────────────────────────────────────
  | 'event' | 'Signal'                      // ← legacy alias
  | 'notification' | 'TimelineItem'         // ← legacy alias
  | 'trace' | 'ExecutionResult'             // ← legacy alias
  | 'checkpoint' | 'JobStatus'              // ← legacy alias
  | 'transaction'
  // ── OPTIMIZE phase (5) ─────────────────────────────────────────────
  | 'audit'
  | 'analytics' | 'OptimizationRecommendation'  // ← legacy alias
  | 'schedule' | 'Schedule'                 // ← legacy alias
  | 'resource' | 'Resource'                 // ← legacy alias
  | 'integration' | 'Integration';          // ← legacy alias

/**
 * Base node data structure - data-driven from database
 * All fields preserved for life-critical auditing
 */
export interface BaseNodeData {
  // Core identification (from database)
  id: string;
  type: PrimitiveWorkflowType;
  label: string;
  description?: string;

  // Runtime status (from execution context)
  status?: ExecutionStatus;

  // Metadata (preserved from database - may be used by custom renderers)
  metadata?: Record<string, unknown>;

  // Execution data (from runtime/replay)
  executionResult?: ExecutionResult;

  // Performance metrics (from monitoring)
  metrics?: NodeMetrics;

  // Validation errors (from validation engine)
  errors?: ValidationError[];

  // Audit trail (life-critical tracking)
  auditInfo?: AuditInfo;

  // Custom renderer configuration (data-driven)
  renderConfig?: NodeRenderConfig;

  // Input/Output schemas (for data flow validation)
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;

  // Compliance flags (life-critical)
  complianceFlags?: string[];

  // Actor information (actor-first architecture)
  actorContext?: ActorContext;
}

/**
 * Base edge data structure - data-driven from database
 */
export interface BaseEdgeData {
  // Core identification
  id: string;
  label?: string;

  // Condition/trigger (from workflow definition)
  condition?: string;

  // Metadata (preserved from database)
  metadata?: Record<string, unknown>;

  // Execution statistics (from monitoring)
  executionCount?: number;
  averageLatency?: number;
  probability?: number;

  // Validation errors
  errors?: ValidationError[];

  // Audit trail
  auditInfo?: AuditInfo;

  // Custom renderer configuration
  renderConfig?: EdgeRenderConfig;
}

/**
 * Execution status (from backend state machine)
 */
export enum ExecutionStatus {
  Pending = 'pending',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Skipped = 'skipped',
  TimedOut = 'timedout',
  RequiresHuman = 'requires_human',
  Paused = 'paused',
  Cancelled = 'cancelled',
}

/**
 * Execution result (from execution engine)
 */
export interface ExecutionResult {
  duration?: number;
  confidence?: number;
  data?: unknown;
  error?: string;
  errorCode?: string;
  assignedTo?: string;
  assignedAt?: Date;
  finalOutcome?: string;
  decisionPath?: string;
  outputs?: Record<string, unknown>;
}

/**
 * Node metrics (from performance monitoring)
 */
export interface NodeMetrics {
  executionTime?: number;
  resourceUsage?: {
    cpu?: number;
    memory?: number;
    network?: number;
  };
  errorCount?: number;
  successCount?: number;
  throughput?: number;
  p50Latency?: number;
  p95Latency?: number;
  p99Latency?: number;
}

/**
 * Validation error (from validation engine)
 */
export interface ValidationError {
  message: string;
  severity?: 'error' | 'warning' | 'info';
  code?: string;
  field?: string;
  nodeId?: string;
  edgeId?: string;
}

/**
 * Audit information (life-critical tracking)
 */
export interface AuditInfo {
  createdAt?: Date;
  createdBy?: string;
  updatedAt?: Date;
  updatedBy?: string;
  version?: number;
  changeHistory?: AuditEntry[];
}

export interface AuditEntry {
  timestamp: Date;
  actor: string;
  action: string;
  changes?: Record<string, unknown>;
}

/**
 * Actor context (actor-first architecture)
 */
export interface ActorContext {
  actorId?: string;
  actorType?: string;
  actorName?: string;
  permissions?: string[];
  roles?: string[];
}

/**
 * Node render configuration (data-driven styling)
 */
export interface NodeRenderConfig {
  color?: string;
  icon?: string;
  size?: 'small' | 'medium' | 'large';
  variant?: string;
  badge?: string;
  showMetrics?: boolean;
  showErrors?: boolean;
  customClassName?: string;
}

/**
 * Edge render configuration (data-driven styling)
 */
export interface EdgeRenderConfig {
  color?: string;
  strokeWidth?: number;
  animated?: boolean;
  style?: 'solid' | 'dashed' | 'dotted';
  markerType?: 'arrow' | 'arrowclosed' | 'circle';
  customClassName?: string;
}

/**
 * Canvas configuration (data-driven behavior)
 */
export interface CanvasConfig {
  // Display options
  showMiniMap?: boolean;
  showControls?: boolean;
  showBackground?: boolean;
  fitView?: boolean;

  // Interaction options
  nodesDraggable?: boolean;
  nodesConnectable?: boolean;
  nodesFocusable?: boolean;
  edgesFocusable?: boolean;

  // Selection options
  multiSelectionActive?: boolean;
  selectionMode?: 'partial' | 'full';

  // Validation options
  validateOnConnect?: boolean;
  validateOnDrop?: boolean;

  // Layout options
  autoLayout?: boolean;
  layoutDirection?: 'TB' | 'LR' | 'RL' | 'BT';

  // Performance options
  onlyRenderVisibleElements?: boolean;

  // Custom behavior hooks
  onNodeClick?: (nodeId: string, nodeData: BaseNodeData) => void;
  onEdgeClick?: (edgeId: string, edgeData: BaseEdgeData) => void;
  onNodeDoubleClick?: (nodeId: string, nodeData: BaseNodeData) => void;
  onEdgeDoubleClick?: (edgeId: string, edgeData: BaseEdgeData) => void;
  onSelectionChange?: (nodeIds: string[], edgeIds: string[]) => void;
  onConnect?: (
    sourceId: string,
    targetId: string,
  ) => void | Promise<void> | boolean | Promise<boolean>;
  onNodesDelete?: (nodeIds: string[]) => void;
  onEdgesDelete?: (edgeIds: string[]) => void;
  onNodeDragStop?: (nodeId: string, position: { x: number; y: number }) => void;
}

/**
 * Typed React Flow node with our data structure
 */
export type FlowNode<T extends BaseNodeData = BaseNodeData> = ReactFlowNode<T>;

/**
 * Typed React Flow edge with our data structure
 */
export type FlowEdge<T extends BaseEdgeData = BaseEdgeData> = ReactFlowEdge<T>;

/**
 * Canvas data input (from database/API)
 */
export interface CanvasData {
  nodes: FlowNode[];
  edges: FlowEdge[];
  metadata?: Record<string, unknown>;
}

/**
 * Node type registry entry
 * Maps primitive workflow type to React component
 */
export interface NodeTypeDefinition {
  type: PrimitiveWorkflowType;
  component: React.ComponentType<{ data: BaseNodeData; selected?: boolean }>;
  defaultConfig?: NodeRenderConfig;
}

/**
 * Replay state for execution playback
 */
export interface ReplayState {
  isReplaying: boolean;
  currentStep: number;
  totalSteps: number;
  playbackSpeed: number;
  executionHistory: ExecutionStep[];
}

export interface ExecutionStep {
  step: number;
  timestamp: Date;
  nodeId: string;
  status: ExecutionStatus;
  data?: unknown;
  duration?: number;
  inputs?: unknown;
  outputs?: unknown;
}

/**
 * Execution context (live execution tracking)
 */
export interface ExecutionContext {
  executionId: string;
  status: ExecutionStatus;
  startTime: Date;
  endTime?: Date;
  currentPhase?: number;
  completedNodes?: Set<string>;
  failedNodes?: Set<string>;
  runningNodes?: Set<string>;
  pausedNodes?: Set<string>;
  metadata?: Record<string, unknown>;
}
